import { useEffect, type CSSProperties } from "react";
import type { ClientBranding } from "@/features/clients/types";
import { forceTheme } from "@/features/utils/theme";

// Turn a client's branding into inline CSS variables the chat components consume.
// Overriding `--primary` cascades to every `bg-primary`/`text-primary` descendant;
// `--chat-radius` drives bubble / input / launcher corner radius.
export function brandingStyle(branding?: ClientBranding | null): CSSProperties {
  const style: Record<string, string> = {};
  if (branding?.theme_color) {
    // Override the resolved Tailwind theme token directly (bg-primary → var(--color-primary)).
    // Setting the raw --primary alone doesn't cascade through the @theme indirection.
    style["--color-primary"] = branding.theme_color;
    style["--primary"] = branding.theme_color;
    style["--color-ring"] = branding.theme_color;
    style["--ring"] = branding.theme_color;
  }
  if (branding?.radius !== null && branding?.radius !== undefined) {
    style["--chat-radius"] = `${branding.radius}px`;
    // The bottom sheet the dialogs become on a phone: its top corners are the
    // panel's, radius + 4 like the embed frame (`embed.js`).
    style["--rsbs-overlay-rounded"] = `${branding.radius + 4}px`;
  }
  return style as CSSProperties;
}

/**
 * The panel's own corner: the bubbles' radius plus the 4px of air around them, the
 * rule `embed.js` draws its frame with — so the standalone page, the preview and the
 * frame on a tenant's site are the same card.
 */
export const panelRadiusClass = "rounded-[calc(var(--chat-radius,1rem)+4px)]";

/**
 * Brand the whole document, not only the chat's own subtree.
 *
 * `ChatLayout` carries the variables on its root, which covers everything drawn inside
 * it — and nothing portalled out of it: the four dialogs, the call menu, the toasts all
 * render under `<body>`, and showed HeyUp blue on a tenant's pink chat. The public
 * chat owns its page, so it sets them on `<html>` as well, and takes them back when it
 * unmounts (a single-page app can leave `/c/*`).
 *
 * The theme goes the same way. `auto` — the default, and what every tenant had
 * before the setting existed — leaves the visitor's own theme alone; `light` or
 * `dark` impose it, because the frame around the widget is the tenant's page and a
 * dark chat in a white frame reads as broken. `fallbackTheme` is what `embed.js`
 * wrote into the URL, for the first paint, before the branding has arrived.
 */
export function useDocumentBranding(
  branding?: ClientBranding | null,
  fallbackTheme?: string | null,
) {
  const style = brandingStyle(branding);
  const key = JSON.stringify(style);
  const theme = branding ? branding.theme : fallbackTheme;

  useEffect(() => {
    const root = document.documentElement.style;
    const entries = Object.entries(JSON.parse(key) as Record<string, string>);
    for (const [name, value] of entries) root.setProperty(name, value);
    return () => {
      for (const [name] of entries) root.removeProperty(name);
    };
  }, [key]);

  useEffect(() => {
    forceTheme(theme === "light" || theme === "dark" ? theme : null);
    return () => forceTheme(null);
  }, [theme]);
}

// Card shadow depth → Tailwind shadow utility. Flat by default (no shadow) to
// match the rest of the app; branding can opt into a shadow.
export function shadowClass(shadow?: ClientBranding["shadow"] | null): string {
  switch (shadow) {
    case "sm":
      return "shadow-sm";
    case "md":
      return "shadow-lg";
    case "lg":
      return "shadow-2xl";
    case "none":
    default:
      return "";
  }
}
